import { useCallback, useRef } from 'react';

import type { MouseEvent, TouchEvent } from 'react';

type MouseOrTouchEvent<T extends HTMLElement = HTMLElement> = MouseEvent<T> | TouchEvent<T>;
type MouseOrTouchEventHandler<T extends HTMLElement = HTMLElement> = (e: MouseOrTouchEvent<T>) => void;

type OnLongPress<T extends HTMLElement = HTMLElement> = MouseOrTouchEventHandler<T>;

type UseLongPressParams<T extends HTMLElement = HTMLElement> = {
  movementThreshold?: number;
  onClick?: MouseOrTouchEventHandler<T>;
  thresholdMs?: number;
};

const getPosition = <T extends HTMLElement = HTMLElement>(e: MouseOrTouchEvent<T>): { x: number; y: number } => {
  if ('touches' in e && e.touches.length > 0 && e.touches[0]) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  if ('clientX' in e) {
    return { x: e.clientX, y: e.clientY };
  }
  return { x: 0, y: 0 };
};

const useLongPress = <T extends HTMLElement = HTMLElement>(
  onLongPress: OnLongPress<T>,
  { movementThreshold = 10, onClick, thresholdMs = 500 }: UseLongPressParams<T> = {},
) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef(false);

  const start = useCallback(
    (e: MouseOrTouchEvent<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const position = getPosition(e);
      startPositionRef.current = position;
      hasMovedRef.current = false;

      timeoutRef.current = setTimeout(() => {
        if (!hasMovedRef.current) {
          onLongPress(e);
        }
      }, thresholdMs);
    },
    [thresholdMs, onLongPress],
  );

  const checkMovement = useCallback(
    (e: MouseOrTouchEvent<T>) => {
      if (startPositionRef.current && !hasMovedRef.current) {
        const currentPosition = getPosition(e);
        const deltaX = Math.abs(currentPosition.x - startPositionRef.current.x);
        const deltaY = Math.abs(currentPosition.y - startPositionRef.current.y);

        if (deltaX > movementThreshold || deltaY > movementThreshold) {
          hasMovedRef.current = true;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      }
    },
    [movementThreshold],
  );

  const clear = useCallback(
    (e: MouseOrTouchEvent<T>, shouldTriggerClick: boolean = true) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (shouldTriggerClick && onClick && !hasMovedRef.current) {
        onClick(e);
      }

      startPositionRef.current = null;
      hasMovedRef.current = false;
    },
    [onClick],
  );

  return {
    onMouseDown: (e: MouseEvent<T>) => start(e),
    onMouseLeave: (e: MouseEvent<T>) => clear(e, false),
    onMouseMove: (e: MouseEvent<T>) => checkMovement(e),
    onMouseUp: (e: MouseEvent<T>) => clear(e),
    onTouchEnd: (e: TouchEvent<T>) => clear(e),
    onTouchMove: (e: TouchEvent<T>) => checkMovement(e),
    onTouchStart: (e: TouchEvent<T>) => start(e),
  };
};

export { useLongPress };
